/**
 * Styled-components implementation of FormField.
 * Preserves the original styled-system behavior: the root Box handles all
 * styled-system props (px, width, color, bg, responsive mb, …) and supports
 * styled(FormField) extensions. This is the legacy path the Hybrid routes to
 * when styled-components rendering is required.
 */

import React, { useMemo, useState } from "react";
import { useTextContent } from "@sproutsocial/seeds-react-hooks";
import Box from "@sproutsocial/seeds-react-box";
import Label from "@sproutsocial/seeds-react-label";
import Text from "@sproutsocial/seeds-react-text";
import { VisuallyHidden } from "@sproutsocial/seeds-react-visually-hidden";
import type { TypeFormFieldProps } from "./FormFieldTypes";
import { nextFormFieldId } from "./formFieldId";

const FormFieldStyled = React.forwardRef<HTMLDivElement, TypeFormFieldProps>(
  (
    {
      children,
      error,
      helperText,
      id: identifier,
      isInvalid = false,
      label,
      mb = 400,
      qa,
      isLabelHidden = false,
      required,
      ...rest
    },
    ref
  ) => {
    const [id] = useState(identifier || nextFormFieldId());
    const errorId = `Error-${id}`;
    const helperTextId = `HelperText-${id}`;
    const containerText = useTextContent("");
    const errorContainerText = useTextContent("");

    const ariaDescribedby = useMemo(() => {
      const ids = [
        helperText && helperTextId,
        isInvalid && error && errorId,
      ].filter(Boolean);
      return ids.length > 0 ? ids.join(" ") : undefined;
    }, [helperText, helperTextId, isInvalid, error, errorId]);

    return (
      <Box
        ref={ref}
        {...rest}
        {...qa}
        mb={mb}
        data-qa-formfield={
          (qa && qa["data-qa-formfield"]) || id || containerText.current
        }
        data-qa-formfield-isinvalid={isInvalid === true}
      >
        {isLabelHidden ? (
          <VisuallyHidden data-testid="visually-hidden">
            <Label htmlFor={id} required={required}>
              {label}
            </Label>
          </VisuallyHidden>
        ) : (
          <Label mb={helperText ? 100 : 300} htmlFor={id} required={required}>
            {label}
          </Label>
        )}
        {helperText && (
          <Text
            as="p"
            fontSize={200}
            mb={300}
            color="text.subtext"
            id={helperTextId}
          >
            {helperText}
          </Text>
        )}
        {children({
          id,
          isInvalid,
          ariaDescribedby,
          ...(required !== undefined && { required }),
        })}
        {isInvalid && error && (
          <Text
            as="div"
            fontSize={200}
            color="text.error"
            mt={300}
            id={errorId}
            data-qa-formfield-error={
              (qa && qa["data-qa-formfield-error"]) ||
              errorContainerText.current
            }
          >
            {error}
          </Text>
        )}
      </Box>
    );
  }
);

FormFieldStyled.displayName = "FormField";
export default FormFieldStyled;
